import { getImageUrl } from './store';

export type DestinationOption = {
  id: string;
  labelKey: string;
  fallback: string;
};

export type ReservationCardConfig = {
  id: string;
  destinationId: string;
  timeIds: string[];
  priceRangeId: string;
  cuisineIds: string[];
  titleKey: string;
  priceRangeKey: string;
  locationKey: string;
  image: string;
  tagKeys: string[];
  statusKey: string;
  availability?: any[];
  isFullyBooked?: boolean;
  bookingMethods?: BookingMethod[];
  booking_rules?: {
    auto_cancel_hold: boolean;
    channel_ids: string[];
    min_advance_hours: number;
    min_advance_unit: string;
    min_guest_count: number;
    pool_ids: string[];
    require_deposit: boolean;
    store_id: string;
    supplier_code: string;
  };
  bookingRules?: {
    minGuestCount?: number;
    min_guest_count?: number;
  };
};

export type RestaurantMenuItem = {
  id: string;
  name: string;
  category: string;
  image: string;
  price: string;
};

export type RestaurantDetailConfig = ReservationCardConfig & {
  description: string;
  openingHours: string;
  openingPeriods?: any[];
  timeSlots?: any[];
  media?: string[];
  menu: RestaurantMenuItem[];
};

export type ReservationLOVOption = {
  value: string;
  labelKey: string;
  fallback: string;
};

export type ReservationAdvancedFilters = {
  times: string[];
  priceRanges: string[];
  cuisines: string[];
};

export type ReservationSearchCondition = {
  restaurants: string[];
  bookingDate?: string;
  guests: {
    adult: number;
    child: number;
  };
  destinations: string[];
  filters: ReservationAdvancedFilters;
};

export const reservationFilterLOV = {
  times: [
    {
      value: '1',
      labelKey: 'b2b.topup.creationDate.morning',
      fallback: 'Sáng',
    },
    {
      value: '2',
      labelKey: 'b2b.topup.creationDate.afternoon',
      fallback: 'Chiều',
    },
    {
      value: '3',
      labelKey: 'b2b.topup.creationDate.evening',
      fallback: 'Tối',
    },
  ],
  priceRanges: [
    {
      value: '1',
      labelKey: 'b2b.restaurant.filter.price.under100',
      fallback: 'Dưới 100.000đ',
    },
    {
      value: '2',
      labelKey: 'b2b.restaurant.filter.price.100300',
      fallback: '100.000-300.000 đ',
    },
    {
      value: '3',
      labelKey: 'b2b.restaurant.filter.price.300500',
      fallback: '300.000-500.000 đ',
    },
    {
      value: '4',
      labelKey: 'b2b.restaurant.filter.price.over500',
      fallback: 'Trên 500.000 đ',
    },
  ],
  cuisines: [
    {
      value: '1',
      labelKey: 'b2b.restaurant.filter.cuisine.vietnamese',
      fallback: 'Việt',
    },
    {
      value: '2',
      labelKey: 'b2b.restaurant.filter.cuisine.asianEuropean',
      fallback: 'Á-Âu',
    },
    {
      value: '3',
      labelKey: 'b2b.restaurant.filter.cuisine.buffet',
      fallback: 'Buffet',
    },
    {
      value: '4',
      labelKey: 'b2b.restaurant.filter.cuisine.craft',
      fallback: 'Thủ công',
    },
  ],
};

export type RestaurantDetailResponse = {
  code: number;
  data?: RestaurantDetailConfig;
};

/**
 * Các helper bên dưới đóng vai trò adapter giữa response variant của API và
 * shape mà UI nhà hàng đang cần. Backend có thể trả menu/giờ mở cửa bằng nhiều
 * field khác nhau, nên gom logic normalize tại đây để component chỉ nhận dữ
 * liệu đã sẵn sàng render.
 */
export const getVariantMenu = (variant: any, image: string) => {
  const menuItems = Array.isArray(variant?.menu)
    ? variant.menu
    : Array.isArray(variant?.menus)
      ? variant.menus
      : [];

  return menuItems.map((item: any, index: number) => ({
    id: `${variant?.id ?? variant?.variantId ?? 'menu'}-${item?.id ?? index}`,
    name: item?.name ?? item?.title ?? '',
    category: item?.category ?? '',
    image: getImageUrl(item) || image,
    price: item?.price ?? item?.priceText ?? '',
  }));
};

/**
 * Quy đổi chuỗi giờ HH:mm/HH:mm:ss thành số phút trong ngày để so sánh các
 * khung giờ mà không phụ thuộc vào Date object hay ngày hiện tại.
 */
const getTimeMinutes = (time?: string) => {
  if (!time) return undefined;

  const [hour, minute] = time.split(':').map(Number);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return undefined;

  return hour * 60 + minute;
};

/**
 * Đưa một period của API về format hiển thị chung "start - end".
 */
const formatPeriodOpeningHours = (period?: any) => {
  const startTime = period?.start_time ?? period?.startTime;
  const endTime = period?.end_time ?? period?.endTime;

  return startTime && endTime ? `${startTime} - ${endTime}` : undefined;
};

/**
 * Chọn khung giờ phù hợp với thời điểm hiện tại:
 * - đang nằm trong khung nào thì lấy khung đó
 * - nếu đã qua một khung thì lấy khung kế tiếp
 * - nếu đã qua khung cuối thì giữ khung cuối để UI vẫn có giờ tham chiếu
 */
const getCurrentPeriod = (periods: any[]) => {
  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const sortedPeriods = periods
    .filter(period => {
      const startMinutes = getTimeMinutes(
        period?.start_time ?? period?.startTime,
      );
      const endMinutes = getTimeMinutes(period?.end_time ?? period?.endTime);

      return startMinutes !== undefined && endMinutes !== undefined;
    })
    .sort((first, second) => {
      const firstStartMinutes = getTimeMinutes(
        first?.start_time ?? first?.startTime,
      );
      const secondStartMinutes = getTimeMinutes(
        second?.start_time ?? second?.startTime,
      );

      return (firstStartMinutes ?? 0) - (secondStartMinutes ?? 0);
    });

  if (!sortedPeriods.length) return undefined;

  const currentPeriod = sortedPeriods.find(period => {
    const startMinutes = getTimeMinutes(
      period?.start_time ?? period?.startTime,
    );
    const endMinutes = getTimeMinutes(period?.end_time ?? period?.endTime);

    if (startMinutes === undefined || endMinutes === undefined) return false;

    return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
  });

  if (currentPeriod) return currentPeriod;

  return (
    sortedPeriods.find(period => {
      const startMinutes = getTimeMinutes(
        period?.start_time ?? period?.startTime,
      );

      return startMinutes !== undefined && currentMinutes < startMinutes;
    }) ?? sortedPeriods[sortedPeriods.length - 1]
  );
};

/**
 * Trả về giờ hoạt động của variant theo ưu tiên periods trước, vì periods là
 * dữ liệu chi tiết theo buổi. Các field openingHours/openTime-closeTime được
 * giữ làm fallback cho những response cũ hoặc thiếu periods.
 */
export const getVariantOpeningHours = (variant: any) => {
  const periods = Array.isArray(variant?.periods) ? variant.periods : [];
  const currentPeriod = getCurrentPeriod(periods);

  return (
    [
      formatPeriodOpeningHours(currentPeriod),
      variant?.openingHours,
      variant?.opening_hours,
      variant?.openTime && variant?.closeTime
        ? `${variant.openTime} - ${variant.closeTime}`
        : undefined,
    ].find(Boolean) ?? ''
  );
};

export enum BOOKING_METHODS {
  DEPOSIT_RESERVATION = 'DEPOSIT_RESERVATION', //luồng 1901, đặt bàn phải có cọc
  TICKET_RESERVATION = 'TICKET_RESERVATION', //luồng bavaria có slot đặt bàn theo vé
}

export type BookingMethod = {
  code: BOOKING_METHODS;
  name: string;
};

export enum BOOKING_SOURCE {
  SUNWORLD_TICKET = 'SUNWOLRD_TICKET', //đơn hàng đặt chỗ từ luồng mua vé  - vé ngắn ngày
  RESERVED = 'RESERVED', // đơn hàng đặt chỗ từ luồng đặt nhà hàng - vé dài ngày
} //doc: https://sun-group.atlassian.net/wiki/spaces/EC/pages/550831319/SunHolidays+OTA+API+v2+-+Lu+ng+t+b+n+Reservation#3.-POST-/orders/create---T%E1%BA%A1o-%C4%91%C6%A1n-(Reservation)

/**Do data có thể cấu 1 nhà hàng là cả luồng đặt cọc và luồng đặt bàn có vé
 * để đầu FE chủ động thì hàm này sẽ format các giá trị thành ưu tiên lấy 
 * 1. Luồng đặt bàn có vé 
 * 2. Luồng đặt cọc.
 */
export const getBookingMethodArr = (bookingMethods: BookingMethod[] = []) => {
  return bookingMethods.find(
    method => method?.code === BOOKING_METHODS.TICKET_RESERVATION,
  )?.code
    ?? bookingMethods.find(
      method => method?.code === BOOKING_METHODS.DEPOSIT_RESERVATION,
    )?.code
    ?? bookingMethods[0]?.code;
};

export const getBookingMethodStr = (bookingMethods: BOOKING_METHODS[] = []) => {
  if (bookingMethods.includes(BOOKING_METHODS.TICKET_RESERVATION)) { return BOOKING_METHODS.TICKET_RESERVATION} 
  else if (bookingMethods.includes(BOOKING_METHODS.DEPOSIT_RESERVATION)) { return BOOKING_METHODS.DEPOSIT_RESERVATION} 
  else return '' 
};
